import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface Update {
  id: number;
  title: string;
  image: string;
  date: string;
}

const Footer: React.FC = () => {
  const developerLinkedInUrl = process.env.REACT_APP_DEVELOPER_LINKEDIN_URL || 'https://www.linkedin.com/';
  const [latestPosts, setLatestPosts] = useState<Update[]>([]);
  const [email, setEmail] = useState('');
  const [subscribeMessage, setSubscribeMessage] = useState('');
  const [subscribeError, setSubscribeError] = useState('');

  useEffect(() => {
    // Fetch latest 2 posts
    fetch('http://localhost:5000/api/updates?limit=2')
      .then(res => res.json())
      .then(data => {
        // Ensure unique posts (filter duplicates by id)
        const uniquePosts = data.filter((post: Update, index: number, self: Update[]) =>
          index === self.findIndex((p) => p.id === post.id)
        );
        setLatestPosts(uniquePosts.slice(0, 2));
      })
      .catch(error => {
        console.error('Error fetching latest posts:', error);
      });
  }, []);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubscribeMessage('');
    setSubscribeError('');

    if (!email) {
      setSubscribeError('Please enter your email');
      return;
    }

    try {
      const response = await fetch('http://localhost:5000/api/newsletter/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubscribeMessage('Successfully subscribed!');
        setEmail('');
        setTimeout(() => setSubscribeMessage(''), 3000);
      } else {
        setSubscribeError(data.error || 'Failed to subscribe');
        setTimeout(() => setSubscribeError(''), 3000);
      }
    } catch (error) {
      setSubscribeError('Failed to subscribe. Please try again.');
      setTimeout(() => setSubscribeError(''), 3000);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  useEffect(() => {
    // Back to top button functionality
    const backToTop = document.querySelector('.back-to-top');
    
    const handleScroll = () => {
      if (backToTop) {
        if (window.scrollY > 300) {
          backToTop.classList.add('active');
        } else {
          backToTop.classList.remove('active');
        }
      }
    };

    const scrollToTop = (e: Event) => {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    };

    window.addEventListener('scroll', handleScroll);
    if (backToTop) {
      backToTop.addEventListener('click', scrollToTop);
    }

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (backToTop) {
        backToTop.removeEventListener('click', scrollToTop);
      }
    };
  }, []);

  return (
    <>
      {/* Footer area */}
      <footer className="footer-area secondary-bg overflow-hidden">
        <div className="footer__main-wrp">
          <div className="footer__shape-left wow slideInLeft" data-wow-delay="200ms" data-wow-duration="1500ms">
            <img className="footer__shape__animation" src="/assets/images/shape/footer-shape-left.png" alt="shape" />
          </div>
          <div className="footer__shape-right wow slideInRight" data-wow-delay="400ms" data-wow-duration="1500ms">
            <img className="footer__shape__animation-right" src="/assets/images/shape/footer-shape-right.png" alt="shape" />
          </div>
          <div className="container">
            <div className="footer__wrp pt-120 pb-120">
              <div className="row g-4 justify-content-between">
                <div className="col-lg-3 col-sm-6 wow fadeInUp" data-wow-delay="00ms" data-wow-duration="1500ms">
                  <div className="footer__item">
                    <Link to="/" className="logo mb-40">
                      <img src="/assets/images/logo/logo-light.svg" alt="New Life Nepal" />
                    </Link>
                    <p className="text-white">Transforming lives through compassion, education, and sustainable development in Nepal.</p>
                    <div className="btn-one mt-40">
                      <span className="btn-circle"></span>
                      <Link to="/donate" className="btn-inner">
                        <span className="btn-text">DONATE NOW</span>
                      </Link>
                    </div>
                  </div>
                </div>
                <div className="col-lg-2 col-sm-6 wow fadeInUp" data-wow-delay="200ms" data-wow-duration="1500ms">
                  <div className="footer__item">
                    <h3 className="title mb-40 text-white">Quick Links</h3>
                    <ul className="link">
                      <li className="mb-3">
                        <Link to="/about"><i className="fa-light fa-angles-right me-2"></i> About Us</Link>
                      </li>
                      <li className="mb-3">
                        <Link to="/causes"><i className="fa-light fa-angles-right me-2"></i> Our Causes</Link>
                      </li>
                      <li className="mb-3">
                        <Link to="/events"><i className="fa-light fa-angles-right me-2"></i> Upcoming Event</Link>
                      </li>
                      <li className="mb-3">
                        <Link to="/blog"><i className="fa-light fa-angles-right me-2"></i> Latest Blog</Link>
                      </li>
                      <li>
                        <Link to="/contact"><i className="fa-light fa-angles-right me-2"></i> Contact Us</Link>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="col-lg-3 col-sm-6 wow fadeInUp" data-wow-delay="400ms" data-wow-duration="1500ms">
                  <div className="footer__item">
                    <h3 className="title mb-40 text-white">Latest Post</h3>
                    <ul className="post">
                      {latestPosts.length > 0 ? (
                        latestPosts.map((post, index) => (
                          <li key={post.id} className={index === 0 ? 'mb-3' : ''}>
                            <div className="image">
                              <img 
                                src={post.image || '/assets/images/footer/footer-blog1.png'} 
                                alt={post.title}
                                style={{ 
                                  width: '78px', 
                                  height: '78px', 
                                  objectFit: 'cover',
                                  borderRadius: '5px'
                                }}
                              />
                            </div>
                            <div className="con">
                              <span>{formatDate(post.date)}</span>
                              <Link to={`/updates/${post.id}`}>{post.title.length > 25 ? post.title.substring(0, 25) + '..' : post.title}</Link>
                            </div>
                          </li>
                        ))
                      ) : (
                        <>
                          <li className="mb-3">
                            <div className="image">
                              <img src="/assets/images/footer/footer-blog1.png" alt="Latest update thumbnail" />
                            </div>
                            <div className="con">
                              <span>Loading...</span>
                              <Link to="/updates">Latest Updates</Link>
                            </div>
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
                <div className="col-lg-3 col-sm-6 wow fadeInUp" data-wow-delay="600ms" data-wow-duration="1500ms">
                  <div className="footer__item">
                    <h3 className="title mb-40 text-white">Contact Info</h3>
                    <ul className="link info">
                      <li className="mb-3">
                        <a href="tel:+9779851031573"><i className="fa-solid fa-phone me-1 primary-color"></i> +977-9851031573</a>
                      </li>
                      <li className="mb-3">
                        <a href="mailto:newlifenepal071@gmail.com"><i className="fa-sharp fa-solid fa-envelope me-1 primary-color"></i> newlifenepal071@gmail.com</a>
                      </li>
                      <li className="mb-3">
                        <a href="https://maps.google.com/?q=Kathmandu,Nepal" target="_blank" rel="noopener noreferrer"><i className="fa-solid fa-location-dot me-1 primary-color"></i> Kathmandu, Nepal</a>
                      </li>
                    </ul>
                    
                    <h3 className="title mt-40 mb-20 text-white" style={{ fontSize: '18px' }}>Newsletter</h3>
                    <form onSubmit={handleNewsletterSubmit} style={{ position: 'relative', zIndex: 2 }}>
                      <div className="subscribe-form">
                        <input 
                          type="email" 
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Your email address"
                          className="form-control newsletter-input"
                          autoComplete="email"
                          style={{
                            padding: '12px 15px',
                            borderRadius: '5px',
                            border: '1px solid #d1d5db',
                            backgroundColor: '#ffffff',
                            color: '#111827',
                            marginBottom: '10px',
                            width: '100%',
                            opacity: 1,
                            pointerEvents: 'auto'
                          }}
                        />
                        <button 
                          type="submit" 
                          className="btn btn-primary"
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#03A84A',
                            border: 'none',
                            borderRadius: '5px',
                            color: 'white',
                            cursor: 'pointer',
                            width: '100%',
                            fontWeight: '600'
                          }}
                        >
                          Subscribe
                        </button>
                      </div>
                      {subscribeMessage && (
                        <p style={{ color: '#4ade80', fontSize: '14px', marginTop: '10px' }}>{subscribeMessage}</p>
                      )}
                      {subscribeError && (
                        <p style={{ color: '#f87171', fontSize: '14px', marginTop: '10px' }}>{subscribeError}</p>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="footer__copytext">
          <p className="wow fadeInDown" data-wow-delay="400ms" data-wow-duration="1500ms">
            &copy; {new Date().getFullYear()} All Rights Reserved by <Link to="/" className="text-white primary-hover">New Life Nepal</Link>
            <br />
            Designed and developed by <a href={developerLinkedInUrl} target="_blank" rel="noopener noreferrer" className="text-white primary-hover">Bishal Dahal</a>
          </p>
        </div>
      </footer>

      {/* Back to top */}
      <button type="button" className="back-to-top" aria-label="Back to top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
        <div className="scroll-up">
          <svg className="scroll-circle svg-content" width="100%" height="100%" viewBox="-1 -1 102 102">
            <path d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98" />
          </svg>
        </div>
      </button>
    </>
  );
};

export default Footer;
